// src/plugins/vuetify.js
//
// Vuetify is reskinned to match the LensPoint redesign:
// the semantic palette here is the hex approximation of the
// oklch tokens defined in styles/tokens.scss. The custom CSS
// layer reads those tokens directly; Vuetify reads this
// palette. We keep the two in sync by switching themes on
// the same trigger (data-theme attribute on <html>).

// The icon stylesheet is the 138-icon subset imported once in main.ts, not the
// full 7,447-icon @mdi/font sheet. Importing the full sheet here as well put it
// back on the critical path (616 KB of CSS and a 403 KB woff2) regardless of
// what main.ts did.
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
// Components are auto-imported on demand by vite-plugin-vuetify ({ autoImport:
// true } in vite.config.js), so we do NOT globally register `* as components`
// here — that pulled the entire component library into the eager bundle and
// defeated per-component tree-shaking. Directives aren't used directly in any
// template (Vuetify components import the ones they need themselves), so they're
// dropped from global registration too.
import { watch } from 'vue'
import { useStorage } from '@vueuse/core'

// Safely get stored theme with fallback
let storedTheme = { value: 'light' }
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    storedTheme = useStorage('theme-preference', 'light')
  }
} catch (error) {
  console.warn('Failed to access storage for theme preference, using default:', error)
}

// Background-contrast preference ('default' | 'high'). Drives the
// [data-contrast] attribute on <html>, which tokens.scss reads to push the
// page background further from card surfaces in both themes.
let storedContrast = { value: 'default' }
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    storedContrast = useStorage('contrast-preference', 'default')
  }
} catch (error) {
  console.warn('Failed to access storage for contrast preference, using default:', error)
}

// Text size preference ('default' | 'large' | 'larger'). Drives the
// [data-text-size] attribute on <html>; tokens.scss scales the root font size
// from it, and since the build turns every font size into rem
// (scripts/font-rem.mjs) all of the app's text follows.
const TEXT_SIZES = ['default', 'large', 'larger']
let storedTextSize = { value: 'default' }
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    storedTextSize = useStorage('text-size-preference', 'default')
  }
} catch (error) {
  console.warn('Failed to access storage for text size preference, using default:', error)
}

// =========================================================
// Light theme — hex approximations of tokens.scss
// =========================================================
const lightTheme = {
  dark: false,
  colors: {
    background:           '#f0f0f0',  // --bg
    surface:              '#ffffff',  // --surface
    'surface-variant':    '#f5f5f5',  // --surface-2
    'surface-bright':     '#ffffff',
    'on-background':      '#131313',  // --ink
    'on-surface':         '#131313',
    'on-surface-variant': '#333333',  // --ink-2

    primary:              '#131313',  // --ink (buttons read as primary)
    'on-primary':         '#f0f0f0',  // --bg
    secondary:            '#333333',
    'on-secondary':       '#f0f0f0',
    accent:               '#6b6b6b',
    'on-accent':          '#ffffff',

    error:                '#9d3622',  // ≈ oklch(0.50 0.14 25)  — oxblood
    'on-error':           '#ffffff',
    success:              '#1f7d4e',  // ≈ oklch(0.55 0.13 150) — deep forest
    'on-success':         '#ffffff',
    warning:              '#d4a847',  // ≈ oklch(0.78 0.15 75)  — amber
    'on-warning':         '#1a1a1a',
    info:                 '#5b8fd6',  // ≈ oklch(0.66 0.13 245) — blue
    'on-info':            '#ffffff',

    // Legacy aliases retained for existing components.
    'chart-primary':   '#131313',
    'chart-secondary': '#333333',
    'chart-tertiary':  '#6b6b6b',
    'table-header':    '#f5f5f5',
    'table-border':    '#e4e4e4',
    'table-hover':     '#f5f5f5',
    'text-primary':    '#131313',
    'text-secondary':  '#6b6b6b',
    outline:           '#e4e4e4',
  },
}

// =========================================================
// Dark theme
// =========================================================
const darkTheme = {
  dark: true,
  colors: {
    background:           '#111111',
    surface:              '#272727',
    'surface-variant':    '#2f2f2f',
    'surface-bright':     '#383838',
    'on-background':      '#f2f2f2',
    'on-surface':         '#f2f2f2',
    'on-surface-variant': '#d2d2d2',

    primary:              '#f2f2f2',  // ink in dark mode
    'on-primary':         '#111111',
    secondary:            '#d2d2d2',
    'on-secondary':       '#111111',
    accent:               '#8c8c8c',
    'on-accent':          '#111111',

    error:                '#e87262',
    'on-error':           '#111111',
    success:              '#8edf9f',
    'on-success':         '#111111',
    warning:              '#e8c266',
    'on-warning':         '#111111',
    info:                 '#84b1e6',
    'on-info':            '#111111',

    'chart-primary':   '#f2f2f2',
    'chart-secondary': '#d2d2d2',
    'chart-tertiary':  '#8c8c8c',
    'table-header':    '#2f2f2f',
    'table-border':    '#373737',
    'table-hover':     '#2f2f2f',
    'text-primary':    '#f2f2f2',
    'text-secondary':  '#8c8c8c',
    outline:           '#373737',
  },
}

const vuetify = createVuetify({
  theme: {
    defaultTheme: storedTheme.value,
    themes: {
      light: lightTheme,
      dark: darkTheme,
    },
  },
  defaults: {
    // Vuetify's default dialog scroll-strategy locks the <html> element by
    // setting `position: fixed; top: -<scrollY>px`. That breaks our
    // `position: sticky` sidebar (it shifts up by the scroll offset).
    // Using "reposition" keeps the dialog anchored to its target without
    // touching the document position.
    VDialog: {
      scrollStrategy: 'reposition',
    },
    VOverlay: {
      scrollStrategy: 'reposition',
    },
  },
})

// Keep the browser/OS chrome in sync with the app's own theme.
//
// In `display: standalone` Android paints the band above the app from
// theme-color. The manifest can only carry ONE static value, and the app's
// theme lives in localStorage ('theme-preference') rather than tracking
// prefers-color-scheme -- so a media-keyed <meta> can't follow it, which is
// how a dark-mode app ended up under a white header. Drive the meta from the
// same signal that drives data-theme instead.
//
// The band sits directly above TopBar, so it matches TopBar's `--surface`,
// not the page background. Read the token at runtime instead of duplicating
// hexes here: that keeps high-contrast mode correct and stops the values
// drifting from tokens.scss (which is exactly how they went stale before).
const THEME_COLOR_FALLBACK = { light: '#ffffff', dark: '#272727' }

export function applyThemeColor(name) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  const v = name === 'dark' ? 'dark' : 'light'

  // Falls back only if the stylesheet hasn't landed yet (tokens.scss is
  // imported ahead of this plugin in main.ts, so normally it has).
  let color = ''
  try {
    color = getComputedStyle(document.documentElement)
      .getPropertyValue('--surface')
      .trim()
  } catch (_) { /* noop */ }
  if (!color) color = THEME_COLOR_FALLBACK[v]

  try {
    // Older builds shipped two media-keyed metas; drop any leftovers (a
    // service-worker-cached index.html can still carry them) so the single
    // dynamic tag below is unambiguous.
    let target = null
    document.querySelectorAll('meta[name="theme-color"]').forEach((m) => {
      if (m.hasAttribute('media')) m.remove()
      else if (!target) target = m
    })
    if (!target) {
      target = document.createElement('meta')
      target.setAttribute('name', 'theme-color')
      document.head.appendChild(target)
    }
    target.setAttribute('content', color)
  } catch (_) { /* noop */ }
}

// Mirror Vuetify's active theme onto <html data-theme="..."> so
// the custom CSS layer (tokens.scss + components.scss) stays
// aligned with Vuetify's runtime palette.
if (typeof window !== 'undefined') {
  const apply = (name) => {
    const v = name === 'dark' ? 'dark' : 'light'
    document.documentElement.setAttribute('data-theme', v)
    applyThemeColor(v)
  }
  // Contrast first: apply() reads the computed `--surface` for the theme-color
  // meta, and high contrast changes that token in dark mode. Setting it after
  // apply() would seed the browser chrome from the default-contrast value.
  document.documentElement.setAttribute(
    'data-contrast',
    storedContrast.value === 'high' ? 'high' : 'default',
  )
  document.documentElement.setAttribute(
    'data-text-size',
    TEXT_SIZES.includes(storedTextSize.value) ? storedTextSize.value : 'default',
  )
  apply(storedTheme.value)

  // Keep data-theme (and the persisted preference + browser chrome) in sync
  // when something drives Vuetify's theme directly rather than going through
  // setAppTheme() -- e.g. `theme.global.name.value = x` or `theme.change(x)`.
  //
  // This used to be a descriptor monkey-patch on theme.global.name. That was
  // dead code: Vuetify wraps the ref in a Proxy, and a ref's `value` accessor
  // lives on RefImpl.prototype, so getOwnPropertyDescriptor(ref, 'value')
  // returns undefined -- the `if (origSet)` guard was never true and the patch
  // never installed. A watcher does the job without depending on internals.
  try {
    watch(
      () => vuetify.theme.global.name.value,
      (v) => {
        apply(v)
        try { storedTheme.value = v } catch (_) { /* noop */ }
      },
    )
  } catch (err) {
    // Non-fatal -- setAppTheme() still drives data-theme/persistence directly.
  }
}

export default vuetify

export const themeConfig = {
  light: lightTheme,
  dark: darkTheme,
}

// Helper for components that need to drive the theme imperatively
// (the new AppShell + Settings page). Returns the new theme name.
export function setAppTheme(name) {
  const v = name === 'dark' ? 'dark' : 'light'
  if (typeof window !== 'undefined') {
    document.documentElement.setAttribute('data-theme', v)
    applyThemeColor(v)
    try { storedTheme.value = v } catch (_) { /* noop */ }
  }
  try { vuetify.theme.global.name.value = v } catch (_) { /* noop */ }
  return v
}

export function getAppTheme() {
  if (typeof window === 'undefined') return 'light'
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'
}

// Background contrast ('default' | 'high') — Settings → Appearance.
export function setAppContrast(value) {
  const v = value === 'high' ? 'high' : 'default'
  if (typeof window !== 'undefined') {
    document.documentElement.setAttribute('data-contrast', v)
    // High contrast moves dark `--surface` (#272727 -> #242424), so the
    // browser chrome has to be repainted from the new token value.
    applyThemeColor(getAppTheme())
    try { storedContrast.value = v } catch (_) { /* noop */ }
  }
  return v
}

export function getAppContrast() {
  if (typeof window === 'undefined') return 'default'
  return document.documentElement.getAttribute('data-contrast') === 'high' ? 'high' : 'default'
}

// Text size ('default' | 'large' | 'larger') — Settings → Appearance and the
// onboarding theme step. Returns the size actually applied.
export function setAppTextSize(value) {
  const v = TEXT_SIZES.includes(value) ? value : 'default'
  if (typeof window !== 'undefined') {
    document.documentElement.setAttribute('data-text-size', v)
    try { storedTextSize.value = v } catch (_) { /* noop */ }
  }
  return v
}

export function getAppTextSize() {
  if (typeof window === 'undefined') return 'default'
  const v = document.documentElement.getAttribute('data-text-size')
  return TEXT_SIZES.includes(v) ? v : 'default'
}
